/* Chavu — product.js */
(() => {
  'use strict';

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || window.themeSettings.moneyFormat || '${{amount}}';

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    let value = '';
    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      const jsonEl = this.querySelector('[data-product-json]');
      if (!jsonEl) return;
      // Liquid `product.variants | json` is a bare array, not `{ variants: [...] }`
      const parsed = JSON.parse(jsonEl.textContent);
      this.variants = Array.isArray(parsed) ? parsed : parsed.variants || [];
      this.sectionId = this.dataset.section;
      this.addEventListener('change', this.onVariantChange.bind(this));
      this.updateOptionAvailability();
    }

    get selectedOptions() {
      const options = [];
      this.querySelectorAll('fieldset').forEach((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) options.push(checked.value);
      });
      this.querySelectorAll('select[data-option-select]').forEach((select) => {
        options.push(select.value);
      });
      return options;
    }

    get currentVariant() {
      const selected = this.selectedOptions;
      return this.variants.find((variant) =>
        variant.options.every((option, index) => option === selected[index])
      );
    }

    onVariantChange() {
      const variant = this.currentVariant;
      this.updateSelectedLabels();
      this.updateOptionAvailability();
      this.toggleAddButton(variant);
      if (!variant) return;
      this.updateHiddenInput(variant);
      this.updatePrice(variant);
      this.updateMedia(variant);
      this.updateUrl(variant);
      this.updateSku(variant);
    }

    updateSelectedLabels() {
      this.querySelectorAll('fieldset').forEach((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        const labelValue = fieldset.querySelector('[data-selected-value]');
        if (checked && labelValue) labelValue.textContent = checked.value;
      });
    }

    updateOptionAvailability() {
      const selected = this.selectedOptions;
      this.querySelectorAll('fieldset').forEach((fieldset, fieldsetIndex) => {
        const optionIndex = parseInt(fieldset.dataset.optionIndex || fieldsetIndex, 10);
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const testOptions = [...selected];
          testOptions[optionIndex] = input.value;
          const match = this.variants.find((variant) =>
            variant.options.every((option, i) => option === testOptions[i])
          );
          const available = !!(match && match.available);
          input.toggleAttribute('data-unavailable', !available);
        });
      });
    }

    updateHiddenInput(variant) {
      document.querySelectorAll(`#product-form-${this.sectionId} input[name="id"], #product-form-installment-${this.sectionId} input[name="id"]`).forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updatePrice(variant) {
      const container = document.getElementById(`price-${this.sectionId}`);
      if (!container) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      container.classList.toggle('price__sale', onSale);
      const sale = container.querySelector('.price-item--sale');
      const regular = container.querySelector('.price-item--regular');
      if (sale) sale.textContent = formatMoney(variant.price);
      if (regular) {
        regular.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        regular.hidden = !onSale;
      }
      const badge = container.querySelector('.badge--sale');
      if (badge) badge.hidden = !onSale;
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const media = document.querySelector(`[data-media-id="${this.sectionId}-${variant.featured_media.id}"]`);
      if (media) media.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    updateUrl(variant) {
      if (!this.dataset.updateUrl || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
    }

    updateSku(variant) {
      const sku = document.getElementById(`sku-${this.sectionId}`);
      if (!sku) return;
      sku.textContent = variant.sku || '';
      sku.hidden = !variant.sku;
    }

    toggleAddButton(variant) {
      const form = document.getElementById(`product-form-${this.sectionId}`);
      if (!form) return;
      const button = form.querySelector('[name="add"]');
      const buttonText = button ? button.querySelector('span') : null;
      const dynamicCheckout = document.getElementById(`dynamic-checkout-${this.sectionId}`);
      if (!button || !buttonText) return;

      if (!variant) {
        button.setAttribute('disabled', 'disabled');
        buttonText.textContent = window.themeStrings.unavailable;
        if (dynamicCheckout) dynamicCheckout.hidden = true;
      } else if (!variant.available) {
        button.setAttribute('disabled', 'disabled');
        buttonText.textContent = window.themeStrings.soldOut;
        if (dynamicCheckout) dynamicCheckout.hidden = true;
      } else {
        button.removeAttribute('disabled');
        buttonText.textContent = window.themeStrings.addToCart;
        if (dynamicCheckout) dynamicCheckout.hidden = false;
      }
    }
  }
  customElements.define('variant-selects', VariantSelects);

  class ProductModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog') || this;
      document.querySelectorAll(`[data-modal-opener="${this.id}"]`).forEach((opener) => {
        opener.addEventListener('click', () => this.open(opener));
      });
      this.querySelectorAll('[data-modal-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      this.addEventListener('keyup', (e) => e.key === 'Escape' && this.close());
      this.addEventListener('click', (e) => {
        if (e.target === this) this.close();
      });
    }

    open(opener) {
      this.opener = opener;
      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      const mediaId = opener && opener.dataset.mediaTarget;
      if (mediaId) {
        const target = this.querySelector(`[data-modal-media="${mediaId}"]`);
        this.querySelectorAll('[data-modal-media]').forEach((img) => (img.hidden = img !== target));
        if (target) target.hidden = false;
      }
      window.chavu.trapFocus(this, this.querySelector('[data-modal-close]'));
    }

    close() {
      this.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      window.chavu.removeTrapFocus(this.opener);
    }
  }
  customElements.define('product-modal', ProductModal);

  class RelatedProducts extends HTMLElement {
    connectedCallback() {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          this.loadRecommendations();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }

    loadRecommendations() {
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const fresh = html.querySelector('related-products');
          if (fresh && fresh.innerHTML.trim().length) {
            this.innerHTML = fresh.innerHTML;
            document.dispatchEvent(new CustomEvent('chavu:section:reveal', { detail: { target: this } }));
          }
        })
        .catch(() => {});
    }
  }
  customElements.define('related-products', RelatedProducts);

  class RecipientForm extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('[data-recipient-checkbox]');
      this.fields = this.querySelector('[data-recipient-fields]');
      this.offsetInput = this.querySelector('[data-recipient-offset]');
      if (!this.checkbox || !this.fields) return;
      if (this.offsetInput) {
        this.offsetInput.value = new Date().getTimezoneOffset().toString();
      }
      this.checkbox.addEventListener('change', () => this.toggle());
      this.toggle();
    }

    toggle() {
      const enabled = this.checkbox.checked;
      this.fields.hidden = !enabled;
      this.fields.querySelectorAll('input, textarea').forEach((input) => {
        input.disabled = !enabled;
      });
      if (this.offsetInput) this.offsetInput.disabled = !enabled;
    }
  }
  customElements.define('recipient-form', RecipientForm);
})();
